import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    IconButton,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import Swal from 'sweetalert2';
import moment from 'moment';
import useAuth from '../../context/AuthProvider';
import { apiClient } from '../../helper/api';
import { parseAnyDateToDateOnly } from '../../helper/utils';
import {
    AdminDetailResponse,
    FilingScheduleAdminMapping,
} from '../Elements/FilingScheduleAdminSummary';
import {
    applyOptionsSkipToPeriods,
    areAllPeriodsSkipped,
    emptyMappingForm,
    FilingScheduleAdminFormState,
    FilingScheduleAdminMappingForm,
    FilingScheduleAdminPeriodForm,
    FilingScheduleActionType,
    calculateTimePeriodLabel,
    isZeroReportType,
    mapPeriodSkipForSave,
    PREMIUM_REPORT_TYPE,
    resolveTimePeriodLabel,
    scheduleFlagToBoolean,
    ZERO_REPORT_TYPE,
} from '../../models/filingScheduleAdminTypes';

const selectFieldSx = {
    width: '100%',
    '& .MuiSelect-select': { padding: '14.5px 16px' },
    '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px' },
};
const textFieldSx = {
    '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px !important' },
    '& .MuiInputBase-input': { padding: '10px 14px !important' },
};

const sectionLabelSx = {
    color: 'text.secondary',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    display: 'block',
    mb: 1,
};

const majorSectionLabelSx = {
    ...sectionLabelSx,
    fontSize: '0.8rem',
    color: '#0d4a63',
    borderBottom: '2px solid #e2e8f0',
    pb: 0.5,
    mb: 1.5,
};

function ynToBoolean(value: unknown): boolean {
    if (value === true || value === 1) return true;
    const s = String(value ?? '').trim().toLowerCase();
    return s === 'y' || s === 'yes' || s === 'true' || s === '1';
}

function normalizeActionType(value: string | null | undefined): FilingScheduleActionType {
    return String(value || '').trim().toLowerCase() === 'no_action' ? 'no_action' : 'notice';
}

/** Apply ActionType-driven UI state (clears notice-only Pay To / Address / URL flags when no_action; keeps taxAmount, taxRequired, and attachment required flags). */
function applyActionTypeChange(
    mapping: FilingScheduleAdminMappingForm,
    next: FilingScheduleActionType,
): FilingScheduleAdminMappingForm {
    if (next === 'no_action') {
        return {
            ...mapping,
            actionType: 'no_action',
            // NCA Zero must be configured independently — never stay linked to Premium.
            sameAsPremium: false,
            payToRequired: false,
            addressRequired: false,
            urlRequired: false,
            instructionsRequired: false,
        };
    }
    return { ...mapping, actionType: 'notice' };
}

function mappingFromApi(
    row: FilingScheduleAdminMapping | undefined,
    fallbackType: string,
): FilingScheduleAdminMappingForm {
    const base = emptyMappingForm(fallbackType);
    if (!row) return base;
    const invoiceCombined = ynToBoolean(row.reportInvoiceRequired);
    return {
        ...base,
        stateReportTypeMappingId: row.stateReportTypeMappingId ?? null,
        typeOfReport: row.typeOfReport || fallbackType,
        taxAmount: row.taxAmount ?? '',
        payTo: row.payTo ?? '',
        address: row.address ?? '',
        url: row.url ?? '',
        instructions: row.instructions ?? '',
        additionalNotes: row.additionalNotes ?? '',
        actionType: normalizeActionType(row.actionType),
        sameAsPremium: scheduleFlagToBoolean(row.sameAsPremium),
        taxRequired: scheduleFlagToBoolean(row.taxRequired),
        payToRequired: scheduleFlagToBoolean(row.payToRequired),
        addressRequired: scheduleFlagToBoolean(row.addressRequired),
        urlRequired: scheduleFlagToBoolean(row.urlRequired),
        instructionsRequired: scheduleFlagToBoolean(row.instructionsRequired),
        mapNotesRequired: scheduleFlagToBoolean(row.additionalNotesRequired),
        reqReportOrInvoice: invoiceCombined,
        reqRecon: ynToBoolean(row.reconciliationListRequired),
    };
}

/** Hydrate mapping from API and normalize legacy Tax_Amount sentinels after DB backfill. */
function pickLatestMappingRow(
    mappings: FilingScheduleAdminMapping[],
    isMatch: (m: FilingScheduleAdminMapping) => boolean,
): FilingScheduleAdminMapping | undefined {
    return mappings
        .filter(isMatch)
        .sort(
            (a, b) =>
                (b.stateReportTypeMappingId ?? 0) - (a.stateReportTypeMappingId ?? 0),
        )[0];
}

function normalizeMappingFromApi(
    row: FilingScheduleAdminMapping | undefined,
    fallbackType: string,
): FilingScheduleAdminMappingForm {
    const mapped = mappingFromApi(row, fallbackType);
    if (!row) return mapped;

    const actionFromDb = normalizeActionType(row.actionType);
    if (actionFromDb === 'no_action') {
        return applyActionTypeChange(mapped, 'no_action');
    }

    const raw = String(mapped.taxAmount || '').trim();
    const lower = raw.toLowerCase();
    if (lower === 'required field' || lower.startsWith('required field')) {
        return { ...mapped, taxAmount: '', taxRequired: true, actionType: 'notice' };
    }
    if (lower === 'n/a' || lower === 'na') {
        return { ...mapped, taxAmount: '' };
    }
    return mapped;
}

function toDateInputValue(value: string | null | undefined): string {
    return parseAnyDateToDateOnly(value) ?? '';
}

const PERIOD_DUE_DATE_FORMAT = 'MM/DD/YYYY';

/** API/DB dates (ISO, etc.) → MM/DD/YYYY for form state and display. */
function formatPeriodDueDateForForm(value: string | null | undefined): string {
    const iso = parseAnyDateToDateOnly(value);
    if (!iso) return '';
    const m = moment(iso, 'YYYY-MM-DD', true);
    return m.isValid() ? m.format(PERIOD_DUE_DATE_FORMAT) : '';
}

/** Normalize user input to MM/DD/YYYY when parseable. */
function normalizePeriodDueDateMmDdYyyy(value: string | null | undefined): string {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) return '';
    const strict = moment(trimmed, PERIOD_DUE_DATE_FORMAT, true);
    if (strict.isValid()) return strict.format(PERIOD_DUE_DATE_FORMAT);
    return formatPeriodDueDateForForm(trimmed);
}

function toApiDate(value: string): string | null {
    const v = String(value || '').trim();
    return v || null;
}

/** Mirror premium mapping onto zero (values, action, attachments, Required toggles). */
function copyPremiumToZero(
    zero: FilingScheduleAdminMappingForm,
    premium: FilingScheduleAdminMappingForm,
): FilingScheduleAdminMappingForm {
    const mirrored: FilingScheduleAdminMappingForm = {
        ...zero,
        taxAmount: premium.taxAmount,
        payTo: premium.payTo,
        address: premium.address,
        url: premium.url,
        instructions: premium.instructions,
        additionalNotes: premium.additionalNotes,
        actionType: premium.actionType,
        taxRequired: premium.taxRequired,
        payToRequired: premium.payToRequired,
        addressRequired: premium.addressRequired,
        urlRequired: premium.urlRequired,
        instructionsRequired: premium.instructionsRequired,
        mapNotesRequired: premium.mapNotesRequired,
        reqReportOrInvoice: premium.reqReportOrInvoice,
        reqRecon: premium.reqRecon,
        sameAsPremium: true,
    };
    // Same as Premium is not allowed when Zero ends up as NCA — admin must enter Zero fields.
    if (mirrored.actionType === 'no_action') {
        return applyActionTypeChange(mirrored, 'no_action');
    }
    return mirrored;
}

function calcLabels(periodType: string): string[] {
    if (periodType === 'Quarterly') return ['Q1', 'Q2', 'Q3', 'Q4'];
    if (periodType === 'Semi-Annual') return ['H1', 'H2'];
    if (periodType === 'Annual') return ['Annual'];
    return Array.from({ length: 12 }, (_, i) => `M${i + 1}`);
}

function periodCountForType(periodType: string): number {
    return calcLabels(periodType).length;
}

function calculateDueDatesFromSetDay(
    setDay: number,
    periodType: string,
    eom: boolean,
    setMonth = 12,
    rollToFuture = false,
): Record<number, string> {
    const out: Record<number, string> = {};
    const year = new Date().getFullYear();
    const count = periodCountForType(periodType);
    const today = moment().startOf('day');

    const formatDate = (d: Date) => {
        let m = moment(d).startOf('day');
        if (rollToFuture) {
            while (m.isBefore(today)) {
                m = m.clone().add(1, 'year');
            }
        }
        return m.format('MM/DD/YYYY');
    };

    if (periodType === 'Monthly') {
        for (let m = 1; m <= count; m++) {
            const lastDay = new Date(year, m, 0).getDate();
            const day = eom ? lastDay : Math.min(setDay, lastDay);
            out[m] = formatDate(new Date(year, m - 1, day));
        }
    } else if (periodType === 'Quarterly') {
        [3, 6, 9, 12].forEach((month, i) => {
            const lastDay = new Date(year, month, 0).getDate();
            const day = eom ? lastDay : Math.min(setDay, lastDay);
            out[i + 1] = formatDate(new Date(year, month - 1, day));
        });
    } else if (periodType === 'Semi-Annual') {
        [6, 12].forEach((month, i) => {
            const lastDay = new Date(year, month, 0).getDate();
            const day = eom ? lastDay : Math.min(setDay, lastDay);
            out[i + 1] = formatDate(new Date(year, month - 1, day));
        });
    } else if (periodType === 'Annual') {
        const month = Math.max(1, Math.min(12, Math.round(setMonth) || 12));
        const lastDay = new Date(year, month, 0).getDate();
        const day = eom ? lastDay : Math.min(setDay, lastDay);
        out[1] = formatDate(new Date(year, month - 1, day));
    }
    return out;
}

/**
 * Adjust an existing due date's day (and, for Annual, its month) in place while
 * preserving the year. Used when SetDay / SetMonth / EOM change so the period's
 * month and year are never rewritten from the current calendar year. The day is
 * clamped to the last valid day of the resulting month (e.g. SetDay 31 → Feb 28/29,
 * April 30). Returns MM/DD/YYYY, or the original value when it can't be parsed.
 */
function adjustDueDateDayInPlace(
    existingDueDate: string,
    setDay: number,
    eom: boolean,
    newMonth?: number,
): string {
    const parsed = moment(existingDueDate, ['MM/DD/YYYY', 'YYYY-MM-DD'], true);
    if (!parsed.isValid()) return existingDueDate;
    const year = parsed.year();
    const monthIndex =
        newMonth != null
            ? Math.max(1, Math.min(12, Math.round(newMonth))) - 1
            : parsed.month();
    const lastDay = new Date(year, monthIndex + 1, 0).getDate();
    const day = eom ? lastDay : Math.min(setDay, lastDay);
    return moment(new Date(year, monthIndex, day)).format('MM/DD/YYYY');
}

// function inferSetDayFromPeriods(periods: FilingScheduleAdminPeriodForm[]): number {
//     const first = periods[0]?.dueDate;
//     if (!first) return 15;
//     const m = moment(first, ['MM/DD/YYYY', 'YYYY-MM-DD'], true);
//     return m.isValid() ? m.date() : 15;
// }

/** Prefer DB SetDay; fall back to inferring from first period (legacy rows). */
function resolveSetDayFromSchedule(
    dbSetDay: unknown,
    periods: FilingScheduleAdminPeriodForm[],
): number {
    const n = Number(dbSetDay);
    if (Number.isFinite(n) && n >= 1 && n <= 31) {
        return Math.round(n);
    }
    // return inferSetDayFromPeriods(periods);
    return 0;
}

function inferSetMonthFromPeriods(periods: FilingScheduleAdminPeriodForm[]): number {
    const first = periods[0]?.dueDate;
    if (!first) return 12;
    const m = moment(first, ['MM/DD/YYYY', 'YYYY-MM-DD'], true);
    return m.isValid() ? m.month() + 1 : 12;
}

/** Prefer DB SetMonth; fall back to inferring from first period (legacy Annual rows). */
function resolveSetMonthFromSchedule(
    dbSetMonth: unknown,
    periods: FilingScheduleAdminPeriodForm[],
): number {
    const n = Number(dbSetMonth);
    if (Number.isFinite(n) && n >= 1 && n <= 12) {
        return Math.round(n);
    }
    return inferSetMonthFromPeriods(periods);
}

const MONTH_OPTIONS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
];

/** Max selectable day for a month (Feb allows 29; generation clamps to the actual year length). */
function daysInMonthForSelect(setMonth: number): number {
    const month = Math.max(1, Math.min(12, Math.round(setMonth) || 12));
    return [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
}

interface StateOption {
    stateId: number;
    stateName: string;
}

interface Props {
    open: boolean;
    scheduleId: number | null;
    /** Pre-select state when opening Add New (from list filter). */
    initialStateId?: number;
    onClose: () => void;
    onSaved: () => void;
}

function buildEmptyForm(stateId: number, stateName: string): FilingScheduleAdminFormState {
    const periodType = 'Quarterly';
    // Seed value only, to generate starter Calculated Due Dates below — the Set Day field
    // itself stays blank by default (QA request) until the admin types a value.
    const seedDay = 15;
    const setMonth = 12;
    const eom = false;
    const calculated = calculateDueDatesFromSetDay(seedDay, periodType, eom, setMonth, true);
    const labels = calcLabels(periodType);
    const periods = labels.map((label, i) => ({
        periodId: null,
        period: i + 1,
        dueDate: calculated[i + 1] || '',
        timePeriod: calculateTimePeriodLabel(periodType, i + 1),
        label,
        skip: false,
    }));

    return {
        scheduleId: 0,
        stateId,
        stateName,
        scheduleName: '',
        setDay: 0,
        setMonth,
        dueDate: '',
        currentPeriodType: periodType,
        requestedPeriodType: periodType,
        periodTypePending: null,
        periodTypeEffDate: null,
        payment: true,
        report: true,
        paymentType: 'Optins',
        wetSignature: false,
        notaryStamp: false,
        zeroIndividual: false,
        zeroEntity: false,
        eom: false,
        skip: false,
        notes: '',
        reportingEffectiveDate: moment().format('YYYY-MM-DD'),
        deactivationEffectiveDate: '',
        periodTypeEffectiveDate: moment().format('YYYY-MM-DD'),
        disableZeroReport: false,
        periods,
        premiumMapping: emptyMappingForm(PREMIUM_REPORT_TYPE),
        zeroMapping: emptyMappingForm(ZERO_REPORT_TYPE),
    };
}

/** Field + Required toggle row. Toggle applies to filer File Now only, not admin save. */
const FieldWithRequired: React.FC<{
    label: string;
    value: string;
    onChange: (v: string) => void;
    required: boolean;
    onRequiredChange: (v: boolean) => void;
    /** Disables value input only (Required toggle stays editable unless disabled is set). */
    valuesDisabled?: boolean;
    /** Disables both value input and Required toggle (e.g. Same as Premium on zero report). */
    disabled?: boolean;
    disableRequiredToggle?: boolean;
    multiline?: boolean;
    rows?: number;
    placeholder?: string;
    error?: boolean;
    helperText?: string;
}> = ({
    label,
    value,
    onChange,
    required,
    onRequiredChange,
    valuesDisabled: valuesDisabledProp,
    disabled,
    disableRequiredToggle,
    multiline,
    rows,
    placeholder,
    error,
    helperText,
}) => {
    const valuesDisabled = valuesDisabledProp ?? disabled;
    const toggleDisabled = disableRequiredToggle ?? disabled;
    return (
    <Box>
        <FormHelperText sx={{ fontWeight: 600, mb: 0.5 }}>{label}</FormHelperText>
        <Box sx={{ display: 'flex', gap: 1, alignItems: multiline ? 'flex-start' : 'center' }}>
            <TextField
                fullWidth
                multiline={multiline}
                minRows={rows}
                disabled={valuesDisabled}
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                error={error}
                helperText={helperText}
                sx={textFieldSx}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 72, pt: multiline ? 1 : 0 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 11 }}>
                    Required
                </Typography>
                <Switch
                    size="small"
                    disabled={toggleDisabled}
                    checked={required}
                    onChange={(e) => onRequiredChange(e.target.checked)}
                />
            </Box>
        </Box>
    </Box>
    );
};

export const AddEditFilingScheduleAdmin: React.FC<Props> = ({
    open,
    scheduleId,
    initialStateId = 0,
    onClose,
    onSaved,
}) => {
    const { user } = useAuth();
    const token = user?.token;
    const isAddMode = scheduleId == null || scheduleId <= 0;

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [form, setForm] = useState<FilingScheduleAdminFormState | null>(null);
    const [states, setStates] = useState<StateOption[]>([]);
    const [disableZeroConfirmOpen, setDisableZeroConfirmOpen] = useState(false);
    const [premiumUrlError, setPremiumUrlError] = useState(false);
    const [zeroUrlError, setZeroUrlError] = useState(false);

    useEffect(() => {
        if (!token) return;
        apiClient('/States/getStates', {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            }),
        }).then((res) => {
            if (res?.data) setStates(res.data);
        });
    }, [token]);

    const buildPeriodsFromDetail = (
        data: AdminDetailResponse,
        periodType: string,
        setDay: number,
        eom: boolean,
        setMonth: number,
    ): FilingScheduleAdminPeriodForm[] => {
        const labels = calcLabels(periodType);
        const existing = data.periods || [];
        const calculated = calculateDueDatesFromSetDay(setDay, periodType, eom, setMonth);
        if (existing.length > 0) {
            const maxCount = periodCountForType(periodType);
            const sortedExisting = [...existing].sort(
                (a, b) => (Number(a.period) || 0) - (Number(b.period) || 0),
            );
            const validExisting = sortedExisting.filter((p, i) => {
                const periodNum = Number(p.period) || i + 1;
                return periodNum <= maxCount;
            });
            return validExisting.map((p, i) => {
                const label = labels[i] || `P${i + 1}`;
                const periodNum = Number(p.period) || i + 1;
                const fromApi = formatPeriodDueDateForForm(p.dueDate);
                return {
                    periodId: p.periodId ?? null,
                    period: periodNum,
                    dueDate: fromApi || calculated[i + 1] || '',
                    timePeriod: resolveTimePeriodLabel(
                        periodType,
                        periodNum,
                        (p.timePeriod || '').trim(),
                    ),
                    label,
                    skip: !!p.skip,
                };
            });
        }
        return labels.map((label, i) => ({
            periodId: null,
            period: i + 1,
            dueDate: calculated[i + 1] || '',
            timePeriod: calculateTimePeriodLabel(periodType, i + 1),
            label,
            skip: false,
        }));
    };

    const hydrateFromDetail = useCallback((data: AdminDetailResponse) => {
        const s = data.schedule!;
        const premiumRow = pickLatestMappingRow(
            data.mappings,
            (m) => !isZeroReportType(m.typeOfReport),
        );
        const zeroRow = pickLatestMappingRow(
            data.mappings,
            (m) => isZeroReportType(m.typeOfReport),
        );
        const periodType = s.periodType || 'Quarterly';
        const apiPeriodStubs =
            data.periods?.map((p, i) => ({
                periodId: p.periodId ?? null,
                period: Number(p.period) || i + 1,
                dueDate: formatPeriodDueDateForForm(p.dueDate),
                timePeriod: p.timePeriod || '',
                label: '',
                skip: !!p.skip,
            })) || [];
        const setDay = resolveSetDayFromSchedule(s.setDay, apiPeriodStubs);
        // Annual month: prefer stored SetMonth, else infer from the existing due date.
        const setMonth = resolveSetMonthFromSchedule(s.setMonth ?? null, apiPeriodStubs);
        const periods = buildPeriodsFromDetail(
            data,
            periodType,
            setDay,
            !!s.endOfMonth,
            setMonth,
        );
        setForm({
            scheduleId: s.scheduleId,
            stateId: s.stateId,
            stateName: s.stateName || '',
            scheduleName: s.scheduleName || '',
            setDay,
            setMonth,
            dueDate: periods[0]?.dueDate || '',
            currentPeriodType: periodType,
            requestedPeriodType: s.periodTypePending || periodType,
            periodTypePending: s.periodTypePending,
            periodTypeEffDate: toDateInputValue(s.periodTypeEffectiveDate),
            payment: !!s.payment,
            report: !!s.report,
            paymentType: s.paymentType || '',
            wetSignature: !!s.wetSignature,
            notaryStamp: !!s.notaryStamp,
            zeroIndividual: !!s.zeroIndividual,
            zeroEntity: !!s.zeroEntity,
            eom: !!s.endOfMonth,
            // Options Skip = all periods skipped (kept until product clarifies Options Skip).
            skip: areAllPeriodsSkipped(periods),
            notes: s.notes || '',
            reportingEffectiveDate: toDateInputValue(s.reportingEffectiveDate),
            deactivationEffectiveDate: toDateInputValue(s.deactivationEffectiveDate),
            periodTypeEffectiveDate: toDateInputValue(s.periodTypeEffectiveDate) || moment().format('YYYY-MM-DD'),
            disableZeroReport: !!s.disableZeroReport,
            periods,
            premiumMapping: normalizeMappingFromApi(premiumRow, PREMIUM_REPORT_TYPE),
            zeroMapping: normalizeMappingFromApi(zeroRow, ZERO_REPORT_TYPE),
        });
    }, []);

    useEffect(() => {
        if (!open || !token) {
            setForm(null);
            setLoadError(null);
            return;
        }

        if (isAddMode) {
            const sid = initialStateId > 0 ? initialStateId : 0;
            const st = states.find((s) => s.stateId === sid);
            const stateName = st?.stateName || '';
            setForm(buildEmptyForm(sid, stateName));
            setLoadError(null);
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setLoadError(null);
        apiClient(`/FilingScheduleAdmin/detail?scheduleId=${scheduleId}`, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            }),
        })
            .then((res) => {
                if (cancelled) return;
                if (!res?.status || !res.data?.schedule) {
                    setLoadError(res?.message || 'Failed to load schedule.');
                    return;
                }
                hydrateFromDetail(res.data as AdminDetailResponse);
            })
            .catch((err) => {
                if (!cancelled) setLoadError(err?.message || 'Failed to load schedule.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, scheduleId, token, hydrateFromDetail, isAddMode, initialStateId]);

    useEffect(() => {
        if (!open || !isAddMode || initialStateId <= 0 || states.length === 0) return;
        const st = states.find((s) => s.stateId === initialStateId);
        if (!st) return;
        setForm((prev) => {
            if (!prev || prev.scheduleId !== 0) return prev;
            if (prev.stateId === st.stateId && prev.stateName === st.stateName) return prev;
            return { ...prev, stateId: st.stateId, stateName: st.stateName };
        });
    }, [open, isAddMode, initialStateId, states]);

    const periodTypeChanged = useMemo(() => {
        if (!form || isAddMode) return false;
        return (
            form.requestedPeriodType.trim().toLowerCase()
            !== form.currentPeriodType.trim().toLowerCase()
        );
    }, [form, isAddMode]);

    const showPeriodWarn = periodTypeChanged;

    const patchForm = (patch: Partial<FilingScheduleAdminFormState>) => {
        setForm((prev) => (prev ? { ...prev, ...patch } : prev));
    };

    const recalcPeriods = (next: Partial<FilingScheduleAdminFormState>) => {
        setForm((prev) => {
            if (!prev) return prev;
            const merged = { ...prev, ...next };
            const periodTypeChangedInRecalc =
                next.requestedPeriodType !== undefined
                && next.requestedPeriodType.trim().toLowerCase()
                    !== prev.requestedPeriodType.trim().toLowerCase();

            // Period type changed: rebuild the period structure (count + labels) from scratch.
            if (periodTypeChangedInRecalc) {
                const calculated = calculateDueDatesFromSetDay(
                    merged.setDay,
                    merged.requestedPeriodType,
                    merged.eom,
                    merged.setMonth,
                    true,
                );
                const labels = calcLabels(merged.requestedPeriodType);
                // Period type change rebuilds the grid — do not carry Skip by slot index
                // (e.g. Quarterly Q4 Skip must not become Monthly M4 Skip).
                const periods = labels.map((label, i) => {
                    const existing = merged.periods[i];
                    const periodNum = i + 1;
                    return {
                        periodId: existing?.periodId ?? null,
                        period: periodNum,
                        dueDate: calculated[periodNum] || existing?.dueDate || '',
                        timePeriod: calculateTimePeriodLabel(merged.requestedPeriodType, periodNum),
                        label,
                        skip: false,
                    };
                });
                return {
                    ...merged,
                    periods,
                    skip: false,
                };
            }

            // SetDay / SetMonth / EOM change: keep each period's existing month and year,
            // only adjust the day (and, for Annual, the month). Never rewrite the year.
            const isAnnual = merged.requestedPeriodType === 'Annual';
            const periods = merged.periods.map((existing) => ({
                ...existing,
                dueDate: adjustDueDateDayInPlace(
                    existing.dueDate,
                    merged.setDay,
                    merged.eom,
                    isAnnual ? merged.setMonth : undefined,
                ),
            }));
            return {
                ...merged,
                periods,
                skip: areAllPeriodsSkipped(periods),
            };
        });
    };

    /** Options Skip stays in the UI; it sets Skip on every period until product clarifies. */
    const onOptionsSkipChange = (checked: boolean) => {
        setForm((prev) => {
            if (!prev) return prev;
            const periods = applyOptionsSkipToPeriods(prev.periods, checked);
            return { ...prev, skip: checked, periods };
        });
    };

    const onPeriodSkipChange = (index: number, checked: boolean) => {
        setForm((prev) => {
            if (!prev) return prev;
            const periods = prev.periods.map((p, i) => (
                i === index ? { ...p, skip: checked } : p
            ));
            return {
                ...prev,
                periods,
                skip: areAllPeriodsSkipped(periods),
            };
        });
    };

    const patchPremium = (patch: Partial<FilingScheduleAdminMappingForm>) => {
        setForm((prev) => {
            if (!prev) return prev;
            let premiumMapping =
                patch.actionType !== undefined
                    ? applyActionTypeChange(
                          { ...prev.premiumMapping, ...patch },
                          patch.actionType,
                      )
                    : { ...prev.premiumMapping, ...patch };
            const zeroMapping =
                prev.zeroMapping.sameAsPremium && !prev.disableZeroReport
                    ? copyPremiumToZero(prev.zeroMapping, premiumMapping)
                    : prev.zeroMapping;
            return { ...prev, premiumMapping, zeroMapping };
        });
    };

    const patchZero = (patch: Partial<FilingScheduleAdminMappingForm>) => {
        setForm((prev) => {
            if (!prev) return prev;
            const zeroMapping =
                patch.actionType !== undefined
                    ? applyActionTypeChange(
                          { ...prev.zeroMapping, ...patch },
                          patch.actionType,
                      )
                    : { ...prev.zeroMapping, ...patch };
            return { ...prev, zeroMapping };
        });
    };

    const onSameAsPremiumChange = (checked: boolean) => {
        setForm((prev) => {
            if (!prev) return prev;
            if (checked) {
                return { ...prev, zeroMapping: copyPremiumToZero(prev.zeroMapping, prev.premiumMapping) };
            }
            return { ...prev, zeroMapping: { ...prev.zeroMapping, sameAsPremium: false } };
        });
    };

    const onDisableZeroToggle = (checked: boolean) => {
        if (!form) return;
        if (checked) {
            setDisableZeroConfirmOpen(true);
            return;
        }
        patchForm({ disableZeroReport: false });
    };

    const confirmDisableZero = () => {
        setDisableZeroConfirmOpen(false);
        setZeroUrlError(false);
        patchForm({ disableZeroReport: true });
    };

    const cancelPendingPeriod = async () => {
        if (!form || !token) return;
        const res = await apiClient('/FilingScheduleAdmin/cancelPendingPeriod', {
            method: 'POST',
            headers: new Headers({
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            }),
            body: JSON.stringify({ scheduleId: form.scheduleId }),
        });
        if (!res?.status) return;
        const detailRes = await apiClient(`/FilingScheduleAdmin/detail?scheduleId=${form.scheduleId}`, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            }),
        });
        if (detailRes?.data?.schedule) {
            hydrateFromDetail(detailRes.data as AdminDetailResponse);
        }
    };

    const cancelDeactivation = async () => {
        if (!form || !token || form.scheduleId <= 0) return;
        const confirm = await Swal.fire({
            icon: 'warning',
            title: 'Cancel scheduled deactivation?',
            text: 'The schedule will remain active and reports will keep generating.',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel deactivation',
            cancelButtonText: 'Keep deactivation',
            confirmButtonColor: '#d97706',
        });
        if (!confirm.isConfirmed) return;

        const res = await apiClient('/FilingScheduleAdmin/cancelDeactivation', {
            method: 'POST',
            headers: new Headers({
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            }),
            body: JSON.stringify({ scheduleId: form.scheduleId }),
        });
        if (!res?.status) {
            Swal.fire({
                icon: 'error',
                title: 'Failed',
                text: res?.message || 'Could not cancel scheduled deactivation.',
            });
            return;
        }

        const detailRes = await apiClient(`/FilingScheduleAdmin/detail?scheduleId=${form.scheduleId}`, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            }),
        });
        if (detailRes?.data?.schedule) {
            hydrateFromDetail(detailRes.data as AdminDetailResponse);
        }
    };

    const validateUrls = (): boolean => {
        if (!form) return false;
        const check = (url: string) => {
            const t = url.trim();
            if (!t) return true;
            try {
                return new URL(t).protocol === 'https:';
            } catch {
                return false;
            }
        };
        const premOk = check(form.premiumMapping.url);
        const skipZeroUrlValidation = form.disableZeroReport;
        const zeroOk =
            skipZeroUrlValidation
            || form.zeroMapping.sameAsPremium
            || check(form.zeroMapping.url);
        setPremiumUrlError(!premOk);
        setZeroUrlError(skipZeroUrlValidation ? false : !zeroOk);
        return premOk && zeroOk;
    };

    const handleSave = async () => {
        if (!form || !token) return;
        if (!form.stateId) {
            Swal.fire({ icon: 'warning', title: 'State required', text: 'Select a state before saving.' });
            return;
        }
        if (!form.scheduleName.trim()) {
            Swal.fire({ icon: 'warning', title: 'Report name required', text: 'Enter a report name before saving.' });
            return;
        }
        if (!validateUrls()) {
            Swal.fire({
                icon: 'warning',
                title: 'Invalid URL',
                text: form.disableZeroReport
                    ? 'Premium-bearing URL must be a valid https:// address.'
                    : 'Each URL must be a valid https:// address (or leave blank when optional).',
            });
            return;
        }

        if (showPeriodWarn && !form.periodTypeEffectiveDate) {
            Swal.fire({
                icon: 'warning',
                title: 'Period type change',
                text: 'Set an effective date before saving.',
            });
            return;
        }

        const payload = {
            scheduleId: form.scheduleId > 0 ? form.scheduleId : 0,
            schedule: {
                stateId: form.stateId,
                scheduleName: form.scheduleName,
                periodType: form.requestedPeriodType,
                currentPeriodType: form.currentPeriodType,
                requestedPeriodType: form.requestedPeriodType,
                endOfMonth: form.eom,
                setDay: form.setDay,
                setMonth: form.requestedPeriodType === 'Annual' ? form.setMonth : null,
                payment: form.payment,
                report: form.report,
                paymentType: form.paymentType,
                wetSignature: form.wetSignature,
                notaryStamp: form.notaryStamp,
                zeroIndividual: form.zeroIndividual,
                zeroEntity: form.zeroEntity,
                notes: form.notes,
                reportingEffectiveDate: toApiDate(form.reportingEffectiveDate),
                deactivationEffectiveDate: toApiDate(form.deactivationEffectiveDate),
                periodTypeEffectiveDate: showPeriodWarn ? toApiDate(form.periodTypeEffectiveDate) : null,
                disableZeroReport: form.disableZeroReport,
            },
            periods: form.periods.map((p) => {
                return {
                    periodId: p.periodId,
                    period: p.period,
                    dueDate: normalizePeriodDueDateMmDdYyyy(p.dueDate),
                    timePeriod: (p.timePeriod || '').trim() || p.label || '',
                    label: p.label,
                    skip: mapPeriodSkipForSave(p),
                };
            }),
            premiumMapping: {
                ...form.premiumMapping,
                paymentType: form.paymentType,
                reportInvoiceRequired: form.premiumMapping.reqReportOrInvoice,
                reconciliationListRequired: form.premiumMapping.reqRecon,
            },
            zeroMapping: form.disableZeroReport
                ? null
                : {
                      ...form.zeroMapping,
                      reportInvoiceRequired: form.zeroMapping.reqReportOrInvoice,
                      reconciliationListRequired: form.zeroMapping.reqRecon,
                  },
        };

        setSaving(true);
        try {
            const res = await apiClient('/FilingScheduleAdmin/save', {
                method: 'POST',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                }),
                body: JSON.stringify(payload),
            });
            if (res?.status) {
                await Swal.fire({
                    icon: 'success',
                    title: isAddMode ? 'Created' : 'Saved',
                    text: res.message || (isAddMode ? 'Schedule created.' : 'Schedule updated.'),
                });
                onSaved();
                onClose();
            } else {
                Swal.fire({ icon: 'error', title: 'Save failed', text: res?.message || 'Unable to save.' });
            }
        } catch (err: unknown) {
            Swal.fire({
                icon: 'error',
                title: 'Save failed',
                text: err instanceof Error ? err.message : 'Unable to save.',
            });
        } finally {
            setSaving(false);
        }
    };

    const actionSelect = (
        value: FilingScheduleActionType,
        onChange: (v: FilingScheduleActionType) => void,
        disabled?: boolean,
        disabledHint?: string,
    ) => (
        <FormControl fullWidth>
            <FormHelperText sx={{ fontWeight: 600 }}>Action</FormHelperText>
            <Select
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value as FilingScheduleActionType)}
                sx={selectFieldSx}
            >
                <MenuItem value="no_action">No Client Action Required</MenuItem>
                <MenuItem value="notice">Notice of State Report</MenuItem>
            </Select>
            {disabled && disabledHint ? (
                <FormHelperText sx={{ mt: 0.5 }}>{disabledHint}</FormHelperText>
            ) : null}
        </FormControl>
    );

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                {loading && <LinearProgress />}
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: 'linear-gradient(135deg, #0d4a63 0%, #15607a 100%)',
                        color: '#fff',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EventNoteOutlinedIcon />
                        <Typography variant="h6" component="span">
                            {isAddMode
                                ? 'Add filing schedule row'
                                : form
                                  ? `${form.stateName}: ${form.scheduleName}`
                                  : 'Edit filing schedule'}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: '#fff' }} aria-label="Close">
                        <CloseOutlinedIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 2, pb: 1 }}>
                    {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
                    {form && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            {form.periodTypePending && form.periodTypeEffDate && (
                                <Alert
                                    severity="warning"
                                    icon={<WarningAmberOutlinedIcon />}
                                    action={
                                        <Button size="small" color="inherit" onClick={cancelPendingPeriod}>
                                            Cancel pending change
                                        </Button>
                                    }
                                >
                                    Pending: {form.currentPeriodType} → {form.periodTypePending} effective{' '}
                                    {moment(form.periodTypeEffDate).format('MM/DD/YYYY')}
                                </Alert>
                            )}

                            {/* Schedule details */}
                            <Box>
                                <Typography sx={sectionLabelSx}>Schedule details</Typography>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormHelperText sx={{ fontWeight: 600 }}>State</FormHelperText>
                                            <Select
                                                value={form.stateId || ''}
                                                onChange={(e) => {
                                                    const sid = Number(e.target.value);
                                                    const st = states.find((s) => s.stateId === sid);
                                                    patchForm({
                                                        stateId: sid,
                                                        stateName: st?.stateName || form.stateName,
                                                    });
                                                }}
                                                sx={selectFieldSx}
                                            >
                                                {states.map((st) => (
                                                    <MenuItem key={st.stateId} value={st.stateId}>
                                                        {st.stateName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormHelperText sx={{ fontWeight: 600 }}>Payment Type</FormHelperText>
                                            <TextField
                                                fullWidth
                                                value={form.paymentType}
                                                onChange={(e) => patchForm({ paymentType: e.target.value })}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormHelperText sx={{ fontWeight: 600 }}>Period Type</FormHelperText>
                                            <Select
                                                value={form.requestedPeriodType}
                                                onChange={(e) =>
                                                    recalcPeriods({ requestedPeriodType: e.target.value as string })
                                                }
                                                sx={selectFieldSx}
                                            >
                                                {['Monthly', 'Quarterly', 'Semi-Annual', 'Annual'].map((p) => (
                                                    <MenuItem key={p} value={p}>
                                                        {p}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Grid>
                                        {form.requestedPeriodType !== 'Annual' && (
                                            <Grid item xs={12} sm={6} md={3}>
                                                <FormHelperText sx={{ fontWeight: 600 }}>Set Day</FormHelperText>
                                                <TextField
                                                    fullWidth
                                                    type="number"
                                                    inputProps={{ min: 1, max: 31 }}
                                                    value={form.setDay || ''}
                                                    onChange={(e) => {
                                                        const raw = e.target.value;
                                                        if (raw === '') {
                                                            patchForm({ setDay: 0 });
                                                            return;
                                                        }
                                                        const parsed = parseInt(raw, 10);
                                                        if (!Number.isNaN(parsed) && parsed >= 1 && parsed <= 31) {
                                                            recalcPeriods({ setDay: parsed });
                                                        }
                                                    }}
                                                    sx={textFieldSx}
                                                />
                                            </Grid>
                                        )}
                                        {form.requestedPeriodType === 'Annual' && (
                                            <Grid item xs={12} sm={6} md={3}>
                                                <FormHelperText sx={{ fontWeight: 600 }}>Month</FormHelperText>
                                                <Select
                                                    value={form.setMonth || 12}
                                                    onChange={(e) => {
                                                        const month = Number(e.target.value);
                                                        const clampedDay = Math.min(
                                                            form.setDay || 1,
                                                            daysInMonthForSelect(month),
                                                        );
                                                        recalcPeriods({ setMonth: month, setDay: clampedDay });
                                                    }}
                                                    sx={selectFieldSx}
                                                >
                                                    {MONTH_OPTIONS.map((m) => (
                                                        <MenuItem key={m.value} value={m.value}>
                                                            {m.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </Grid>
                                        )}
                                        {form.requestedPeriodType === 'Annual' && (
                                            <Grid item xs={12} sm={6} md={3}>
                                                <FormHelperText sx={{ fontWeight: 600 }}>Day</FormHelperText>
                                                <TextField
                                                    fullWidth
                                                    type="number"
                                                    inputProps={{ min: 1, max: 31 }}
                                                    value={form.setDay || ''}
                                                    onChange={(e) => {
                                                        const raw = e.target.value;
                                                        if (raw === '') {
                                                            patchForm({ setDay: 0 });
                                                            return;
                                                        }
                                                        const parsed = parseInt(raw, 10);
                                                        if (!Number.isNaN(parsed) && parsed >= 1 && parsed <= 31) {
                                                            recalcPeriods({ setDay: parsed });
                                                        }
                                                    }}
                                                    sx={textFieldSx}
                                                />
                                            </Grid>
                                        )}
                                        <Grid item xs={12}>
                                            <FormHelperText sx={{ fontWeight: 600 }}>Report name</FormHelperText>
                                            <TextField
                                                fullWidth
                                                value={form.scheduleName}
                                                onChange={(e) => patchForm({ scheduleName: e.target.value })}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormHelperText sx={{ fontWeight: 600 }}>Active Effective</FormHelperText>
                                            <TextField
                                                fullWidth
                                                type="date"
                                                InputLabelProps={{ shrink: true }}
                                                value={form.reportingEffectiveDate}
                                                onChange={(e) =>
                                                    patchForm({ reportingEffectiveDate: e.target.value })
                                                }
                                                sx={textFieldSx}
                                                helperText="Edit each period's due date in the Calculated due dates section below."
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Box>

                            {showPeriodWarn && (
                                <Box>
                                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                                        Changing Period Type affects due date calculations and period labels.
                                        Queue rows with due dates before the effective date keep their current labels;
                                        rows on or after the effective date will use the new period type when saved
                                        (or automatically on the effective date if it is in the future).
                                    </Alert>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <FormHelperText sx={{ fontWeight: 600 }}>
                                                Effective date <span style={{ color: '#d32f2f' }}>*</span>
                                            </FormHelperText>
                                            <TextField
                                                fullWidth
                                                type="date"
                                                InputLabelProps={{ shrink: true }}
                                                value={form.periodTypeEffectiveDate}
                                                onChange={(e) =>
                                                    patchForm({ periodTypeEffectiveDate: e.target.value })
                                                }
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {/* Scheduled deactivation */}
                            {!isAddMode && (
                                <Box>
                                    <Typography sx={sectionLabelSx}>Scheduled deactivation</Typography>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                                        {form.deactivationEffectiveDate &&
                                            moment(form.deactivationEffectiveDate).isSameOrAfter(moment(), 'day') && (
                                            <Alert
                                                severity="warning"
                                                icon={<WarningAmberOutlinedIcon />}
                                                action={
                                                    <Button size="small" color="inherit" onClick={cancelDeactivation}>
                                                        Cancel deactivation
                                                    </Button>
                                                }
                                                sx={{ mb: 1.5 }}
                                            >
                                                Scheduled to deactivate on{' '}
                                                {moment(form.deactivationEffectiveDate).format('MM/DD/YYYY')}.
                                                Outstanding (non-filed) reports on or after that date will be removed.
                                            </Alert>
                                        )}

                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={8}>
                                                <FormHelperText sx={{ fontWeight: 600 }}>
                                                    Deactivation effective date
                                                </FormHelperText>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 1.5,
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <TextField
                                                        fullWidth
                                                        type="date"
                                                        InputLabelProps={{ shrink: true }}
                                                        inputProps={{
                                                            min: moment().format('YYYY-MM-DD'),
                                                        }}
                                                        value={form.deactivationEffectiveDate}
                                                        onChange={(e) =>
                                                            patchForm({
                                                                deactivationEffectiveDate: e.target.value,
                                                            })
                                                        }
                                                        sx={textFieldSx}
                                                    />
                                                    {form.deactivationEffectiveDate && (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="warning"
                                                            onClick={() =>
                                                                patchForm({ deactivationEffectiveDate: '' })
                                                            }
                                                            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                                                        >
                                                            Clear date
                                                        </Button>
                                                    )}
                                                </Box>
                                                <FormHelperText sx={{ mx: 0, mt: 0.5 }}>
                                                    Leave blank to keep the schedule active.
                                                </FormHelperText>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Box>
                            )}

                            {/* Options */}
                            <Box>
                                <Typography sx={sectionLabelSx}>Options</Typography>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                                        <FormControlLabel control={<Checkbox checked={form.payment} onChange={(e) => patchForm({ payment: e.target.checked })} />} label="Payment" />
                                        <FormControlLabel control={<Checkbox checked={form.report} onChange={(e) => patchForm({ report: e.target.checked })} />} label="Reports" />
                                        <FormControlLabel control={<Checkbox checked={form.wetSignature} onChange={(e) => patchForm({ wetSignature: e.target.checked })} />} label="Wet Signature" />
                                        <FormControlLabel control={<Checkbox checked={form.notaryStamp} onChange={(e) => patchForm({ notaryStamp: e.target.checked })} />} label="Notary Stamp" />
                                        <FormControlLabel control={<Checkbox checked={form.zeroEntity} onChange={(e) => patchForm({ zeroEntity: e.target.checked })} />} label="Zero Entity" />
                                        <FormControlLabel control={<Checkbox checked={form.zeroIndividual} onChange={(e) => patchForm({ zeroIndividual: e.target.checked })} />} label="Zero Individual" />
                                        <FormControlLabel control={<Checkbox checked={form.eom} onChange={(e) => recalcPeriods({ eom: e.target.checked })} />} label="EOM (End of Month)" />
                                        <FormControlLabel control={<Checkbox checked={form.skip} onChange={(e) => onOptionsSkipChange(e.target.checked)} />} label="Skip" />
                                    </Box>
                                    <FormHelperText sx={{ fontWeight: 600 }}>Notes</FormHelperText>
                                    <TextField fullWidth multiline minRows={4} value={form.notes} onChange={(e) => patchForm({ notes: e.target.value })} sx={textFieldSx} />
                                </Paper>
                            </Box>

                            {/* Premium-bearing */}
                            <Box>
                                <Typography sx={majorSectionLabelSx}>Premium-bearing</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {actionSelect(form.premiumMapping.actionType, (v) => patchPremium({ actionType: v }))}
                                    {form.premiumMapping.actionType === 'no_action' && (
                                        <Alert severity="info" sx={{ py: 0.5 }}>
                                            No Client Action Required — MISC completes via Filed Notes and the
                                            report bills (zero or premium fee from Tax amount). Client messaging
                                            comes from Instructions / Additional notes below.
                                        </Alert>
                                    )}
                                    {form.premiumMapping.actionType !== 'no_action' && (
                                    <>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <FieldWithRequired
                                                label="Tax amount"
                                                placeholder="e.g. 3, $300, See Attached Schedule"
                                                value={form.premiumMapping.taxAmount}
                                                onChange={(v) => patchPremium({ taxAmount: v })}
                                                required={form.premiumMapping.taxRequired}
                                                onRequiredChange={(v) => patchPremium({ taxRequired: v })}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <FieldWithRequired
                                                label="Pay to"
                                                value={form.premiumMapping.payTo}
                                                onChange={(v) => patchPremium({ payTo: v })}
                                                required={form.premiumMapping.payToRequired}
                                                onRequiredChange={(v) => patchPremium({ payToRequired: v })}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FieldWithRequired
                                                label="Address"
                                                placeholder="Mailing address for check payments"
                                                value={form.premiumMapping.address}
                                                onChange={(v) => patchPremium({ address: v })}
                                                required={form.premiumMapping.addressRequired}
                                                onRequiredChange={(v) => patchPremium({ addressRequired: v })}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FieldWithRequired
                                                label="URL"
                                                placeholder="https://"
                                                value={form.premiumMapping.url}
                                                onChange={(v) => patchPremium({ url: v })}
                                                required={form.premiumMapping.urlRequired}
                                                onRequiredChange={(v) => patchPremium({ urlRequired: v })}
                                                error={premiumUrlError}
                                                helperText={premiumUrlError ? 'URL must use https://' : ''}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FieldWithRequired
                                                label="Instructions"
                                                placeholder="Client-facing filing instructions…"
                                                value={form.premiumMapping.instructions}
                                                onChange={(v) => patchPremium({ instructions: v })}
                                                required={form.premiumMapping.instructionsRequired}
                                                onRequiredChange={(v) => patchPremium({ instructionsRequired: v })}
                                                multiline
                                                rows={5}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FieldWithRequired
                                                label="Additional notes"
                                                value={form.premiumMapping.additionalNotes}
                                                onChange={(v) => patchPremium({ additionalNotes: v })}
                                                required={form.premiumMapping.mapNotesRequired}
                                                onRequiredChange={(v) => patchPremium({ mapNotesRequired: v })}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b' }}>
                                        Required Attachments
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        <FormControlLabel control={<Checkbox checked={form.premiumMapping.reqReportOrInvoice} onChange={(e) => patchPremium({ reqReportOrInvoice: e.target.checked })} />} label="Report or Invoice" />
                                        <FormControlLabel control={<Checkbox checked={form.premiumMapping.reqRecon} onChange={(e) => patchPremium({ reqRecon: e.target.checked })} />} label="Reconciliation" />
                                    </Box>
                                    </>
                                    )}
                                    {form.premiumMapping.actionType === 'no_action' && (
                                    <>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <FieldWithRequired
                                                label="Tax amount"
                                                placeholder="e.g. 3, $300, See Attached Schedule"
                                                value={form.premiumMapping.taxAmount}
                                                onChange={(v) => patchPremium({ taxAmount: v })}
                                                required={form.premiumMapping.taxRequired}
                                                onRequiredChange={(v) => patchPremium({ taxRequired: v })}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FieldWithRequired
                                                label="Instructions"
                                                placeholder="Client-facing message (e.g. completed on your behalf)…"
                                                value={form.premiumMapping.instructions}
                                                onChange={(v) => patchPremium({ instructions: v })}
                                                required={form.premiumMapping.instructionsRequired}
                                                onRequiredChange={(v) => patchPremium({ instructionsRequired: v })}
                                                multiline
                                                rows={3}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FieldWithRequired
                                                label="Additional notes"
                                                value={form.premiumMapping.additionalNotes}
                                                onChange={(v) => patchPremium({ additionalNotes: v })}
                                                required={form.premiumMapping.mapNotesRequired}
                                                onRequiredChange={(v) => patchPremium({ mapNotesRequired: v })}
                                                multiline
                                                rows={2}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b' }}>
                                        Required Attachments
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        <FormControlLabel control={<Checkbox checked={form.premiumMapping.reqReportOrInvoice} onChange={(e) => patchPremium({ reqReportOrInvoice: e.target.checked })} />} label="Report or Invoice" />
                                        <FormControlLabel control={<Checkbox checked={form.premiumMapping.reqRecon} onChange={(e) => patchPremium({ reqRecon: e.target.checked })} />} label="Reconciliation" />
                                    </Box>
                                    </>
                                    )}
                                </Box>
                            </Box>

                            {/* Zero report */}
                            <Box>
                                <Typography sx={majorSectionLabelSx}>Zero report</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                    <Typography sx={{ fontWeight: 600 }}>Disable Zero Report?</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>Yes</Typography>
                                        <Switch
                                            checked={form.disableZeroReport}
                                            onChange={(e) => onDisableZeroToggle(e.target.checked)}
                                        />
                                    </Box>
                                </Box>

                                {!form.disableZeroReport && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {actionSelect(
                                                form.zeroMapping.sameAsPremium
                                                    ? form.premiumMapping.actionType
                                                    : form.zeroMapping.actionType,
                                                (v) => patchZero({ actionType: v }),
                                                form.zeroMapping.sameAsPremium,
                                                'Action follows Premium-Bearing while Same as Premium is checked. Uncheck Same as Premium to set Zero Report action separately.',
                                            )}
                                            {form.zeroMapping.actionType === 'no_action' && (
                                                <Alert severity="info" sx={{ py: 0.5 }}>
                                                    No Client Action Required — MISC completes via Filed Notes; bills zero or premium fee from Tax amount.
                                                </Alert>
                                            )}
                                            {form.zeroMapping.actionType !== 'no_action' && (
                                            <>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={form.zeroMapping.sameAsPremium}
                                                        onChange={(e) => onSameAsPremiumChange(e.target.checked)}
                                                    />
                                                }
                                                label="Same as Premium?"
                                            />
                                            {form.zeroMapping.sameAsPremium && (
                                                <Alert severity="info" sx={{ py: 0.5 }}>
                                                    Same as Premium — action, field values, and Required settings
                                                    mirror Premium-Bearing. Uncheck to configure Zero Report separately.
                                                </Alert>
                                            )}
                                            </>
                                            )}
                                            {(form.zeroMapping.sameAsPremium
                                                ? form.premiumMapping.actionType !== 'no_action'
                                                : form.zeroMapping.actionType !== 'no_action') && (
                                            <>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <FieldWithRequired
                                                        label="Tax amount"
                                                        placeholder="Enter 0 for Zero Reports"
                                                        value={form.zeroMapping.taxAmount}
                                                        onChange={(v) => patchZero({ taxAmount: v })}
                                                        required={form.zeroMapping.taxRequired}
                                                        onRequiredChange={(v) => patchZero({ taxRequired: v })}
                                                        disabled={form.zeroMapping.sameAsPremium}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <FieldWithRequired
                                                        label="Pay to"
                                                        value={form.zeroMapping.payTo}
                                                        onChange={(v) => patchZero({ payTo: v })}
                                                        required={form.zeroMapping.payToRequired}
                                                        onRequiredChange={(v) => patchZero({ payToRequired: v })}
                                                        disabled={form.zeroMapping.sameAsPremium}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <FieldWithRequired
                                                        label="Address"
                                                        placeholder="Mailing address for check payments"
                                                        value={form.zeroMapping.address}
                                                        onChange={(v) => patchZero({ address: v })}
                                                        required={form.zeroMapping.addressRequired}
                                                        onRequiredChange={(v) => patchZero({ addressRequired: v })}
                                                        disabled={form.zeroMapping.sameAsPremium}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <FieldWithRequired
                                                        label="URL"
                                                        placeholder="https://"
                                                        value={form.zeroMapping.url}
                                                        onChange={(v) => patchZero({ url: v })}
                                                        required={form.zeroMapping.urlRequired}
                                                        onRequiredChange={(v) => patchZero({ urlRequired: v })}
                                                        disabled={form.zeroMapping.sameAsPremium}
                                                        error={zeroUrlError}
                                                        helperText={zeroUrlError ? 'URL must use https://' : ''}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <FieldWithRequired
                                                        label="Instructions"
                                                        value={form.zeroMapping.instructions}
                                                        onChange={(v) => patchZero({ instructions: v })}
                                                        required={form.zeroMapping.instructionsRequired}
                                                        onRequiredChange={(v) => patchZero({ instructionsRequired: v })}
                                                        disabled={form.zeroMapping.sameAsPremium}
                                                        multiline
                                                        rows={3}
                                                    />
                                                </Grid>
                                            </Grid>
                                            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b' }}>
                                                Required Attachments
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                <FormControlLabel control={<Checkbox checked={form.zeroMapping.reqReportOrInvoice} onChange={(e) => patchZero({ reqReportOrInvoice: e.target.checked })} disabled={form.zeroMapping.sameAsPremium} />} label="Report or Invoice" />
                                                <FormControlLabel control={<Checkbox checked={form.zeroMapping.reqRecon} onChange={(e) => patchZero({ reqRecon: e.target.checked })} disabled={form.zeroMapping.sameAsPremium} />} label="Reconciliation" />
                                            </Box>
                                            </>
                                            )}
                                            {!form.zeroMapping.sameAsPremium
                                                && form.zeroMapping.actionType === 'no_action' && (
                                                <>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} md={6}>
                                                        <FieldWithRequired
                                                            label="Tax amount"
                                                            placeholder="Enter 0 for Zero Reports"
                                                            value={form.zeroMapping.taxAmount}
                                                            onChange={(v) => patchZero({ taxAmount: v })}
                                                            required={form.zeroMapping.taxRequired}
                                                            onRequiredChange={(v) => patchZero({ taxRequired: v })}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <FieldWithRequired
                                                            label="Instructions"
                                                            placeholder="Client-facing message…"
                                                            value={form.zeroMapping.instructions}
                                                            onChange={(v) => patchZero({ instructions: v })}
                                                            required={form.zeroMapping.instructionsRequired}
                                                            onRequiredChange={(v) => patchZero({ instructionsRequired: v })}
                                                            multiline
                                                            rows={3}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <FieldWithRequired
                                                            label="Additional notes"
                                                            value={form.zeroMapping.additionalNotes}
                                                            onChange={(v) => patchZero({ additionalNotes: v })}
                                                            required={form.zeroMapping.mapNotesRequired}
                                                            onRequiredChange={(v) => patchZero({ mapNotesRequired: v })}
                                                            multiline
                                                            rows={2}
                                                        />
                                                    </Grid>
                                                </Grid>
                                                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b' }}>
                                                    Required Attachments
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    <FormControlLabel control={<Checkbox checked={form.zeroMapping.reqReportOrInvoice} onChange={(e) => patchZero({ reqReportOrInvoice: e.target.checked })} />} label="Report or Invoice" />
                                                    <FormControlLabel control={<Checkbox checked={form.zeroMapping.reqRecon} onChange={(e) => patchZero({ reqRecon: e.target.checked })} />} label="Reconciliation" />
                                                </Box>
                                                </>
                                            )}
                                    </Box>
                                )}
                            </Box>

                            {/* Calculated due dates */}
                            <Box>
                                <Typography sx={sectionLabelSx}>Calculated due dates</Typography>
                                <FormHelperText sx={{ mx: 0, mt: 0, mb: 1 }}>
                                    Use Skip on a period to exclude it from report generation.
                                </FormHelperText>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                                    <Grid container spacing={2}>
                                        {form.periods.map((p, idx) => (
                                                <Grid item xs={12} sm={6} md={4} key={`calc-${idx}`}>
                                                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                                                        {p.label}
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label="Due date"
                                                        placeholder={PERIOD_DUE_DATE_FORMAT}
                                                        value={p.dueDate}
                                                        onChange={(e) => {
                                                            const periods = [...form.periods];
                                                            periods[idx] = { ...periods[idx], dueDate: e.target.value };
                                                            patchForm({ periods });
                                                        }}
                                                        onBlur={(e) => {
                                                            const normalized = normalizePeriodDueDateMmDdYyyy(
                                                                e.target.value,
                                                            );
                                                            if (!normalized) return;
                                                            const periods = [...form.periods];
                                                            periods[idx] = { ...periods[idx], dueDate: normalized };
                                                            patchForm({ periods });
                                                        }}
                                                        sx={{ ...textFieldSx, mb: 1 }}
                                                    />
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label="Time period"
                                                        placeholder="e.g. January - December, Jan – Mar"
                                                        value={p.timePeriod}
                                                        onChange={(e) => {
                                                            const periods = [...form.periods];
                                                            periods[idx] = { ...periods[idx], timePeriod: e.target.value };
                                                            patchForm({ periods });
                                                        }}
                                                        sx={{ ...textFieldSx, mb: 1 }}
                                                    />
                                                    <FormControlLabel
                                                        control={(
                                                            <Checkbox
                                                                checked={!!p.skip}
                                                                onChange={(e) => onPeriodSkipChange(idx, e.target.checked)}
                                                            />
                                                        )}
                                                        label="Skip"
                                                    />
                                                </Grid>
                                        ))}
                                    </Grid>
                                </Paper>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                    <Button onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={loading || saving || !form}
                        sx={{ backgroundColor: '#2099d2', '&:hover': { backgroundColor: '#176990' } }}
                    >
                        {saving ? 'Saving…' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={disableZeroConfirmOpen}
                onClose={() => setDisableZeroConfirmOpen(false)}
                maxWidth="sm"
                fullWidth
                sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
            >
                <DialogTitle>Disable Zero Report?</DialogTitle>
                <DialogContent>
                    <Typography color="text.secondary">
                        The zero report section will be hidden for this schedule. MISC will not be able to file reports as Zero Report, but can still mark outstanding rows as No Report Required to clear them from the queue.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                    <Button onClick={() => setDisableZeroConfirmOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={confirmDisableZero}
                        sx={{ backgroundColor: '#2099d2', '&:hover': { backgroundColor: '#176990' } }}
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
